import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { api, errorText } from '../api/client'
import type { LoanRow, PipelineResponse } from '../api/types'
import { Icon } from '../components/Icon'
import { dragProps, KanbanColumn } from '../components/KanbanColumn'
import { NewDealModal } from '../components/NewDealModal'
import { ErrorState, Loading } from '../components/States'
import { useToast } from '../components/Toast'
import { addressLine, percent, rate, relativeDays, titleCase, usd, usdShort } from '../lib/format'
import { stageColor } from '../lib/tint'
import { useAsync } from '../lib/useAsync'

const GRID = '1.4fr 1fr 0.8fr 0.7fr 0.7fr 1fr'

/** Kanban over the officer's stages: the LOS vocabulary with their personal
 *  labels, hidden stages and custom stages applied.
 *
 *  Dragging a LendingPad loan records a CRM-side stage that holds only until
 *  LendingPad reports a new status, so the LOS stays the source of truth. The card
 *  shows when its column differs from LendingPad. Pre-LOS opportunities just move. */
export function Pipeline() {
  const [includeTerminal, setIncludeTerminal] = useState(false)
  const [view, setView] = useState<'kanban' | 'table'>('kanban')
  const [dragging, setDragging] = useState(false)
  const [newIn, setNewIn] = useState<string | null>(null)
  const { data, error, loading, reload } = useAsync(
    () => api.pipeline(includeTerminal),
    [includeTerminal],
  )
  const [local, setLocal] = useState<PipelineResponse | null>(null)
  const board = local ?? data
  const nav = useNavigate()
  const toast = useToast()

  const loans = board?.loans ?? []
  const volume = loans.reduce((a, l) => a + (l.loan_amount ?? 0), 0)
  const stages = (board?.stages ?? []).filter((s) => !s.hidden && (includeTerminal || !s.terminal))
  const labelOf = (key: string) => board?.stages.find((s) => s.key === key)?.label ?? titleCase(key)

  const byStage = new Map<string, LoanRow[]>()
  stages.forEach((s) => byStage.set(s.key, []))
  loans.forEach((l) => { if (l.status && byStage.has(l.status)) byStage.get(l.status)!.push(l) })

  const refresh = () => {
    setLocal(null)
    reload()
  }

  async function move(loanId: string, stage: string) {
    const current = board
    const loan = current?.loans.find((l) => l.loan_id === loanId)
    if (!current || !loan || loan.status === stage) return
    const from = loan.status ?? ''
    const amt = loan.loan_amount ?? 0
    const shift = (t: PipelineResponse['totals'], key: string, n: number) => ({
      ...t,
      [key]: { status: key, loans: (t[key]?.loans ?? 0) + n, volume: (t[key]?.volume ?? 0) + n * amt },
    })
    setLocal({
      ...current,
      loans: current.loans.map((l) => (l.loan_id === loanId
        ? { ...l, status: stage, stage_moved: l.source === 'los' && stage !== l.los_status }
        : l)),
      totals: shift(shift(current.totals, from, -1), stage, 1),
    })
    try {
      const r = await api.moveStage(loanId, stage)
      setLocal((b) => b && {
        ...b,
        loans: b.loans.map((l) => (l.loan_id === loanId ? { ...l, status: r.stage, stage_moved: r.stage_moved } : l)),
      })
      toast(`${loan.borrower ?? 'Loan'} → ${labelOf(stage)}${r.stage_moved ? ' (the loan system still says ' + labelOf(r.los_status ?? '') + ')' : ''}`, {
        tone: 'positive',
        action: from ? { label: 'Undo', onClick: () => move(loanId, from) } : undefined,
      })
    } catch (e) {
      setLocal(current)
      toast(errorText(e), { tone: 'danger' })
    }
  }

  const href = (l: LoanRow) => `/loans/${encodeURIComponent(l.loan_id)}`

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <div className="page-tight">
        <div className="page-head">
          <div>
            <h1 className="h1">LOS Pipeline</h1>
            <p className="subline">
              Live from the loan system. {loans.length} loans · <span className="mono">{usdShort(volume)}</span> volume ·
              drag a card to change its stage until the loan system reports a new one
            </p>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <div className="seg">
              <button className={view === 'kanban' ? 'active' : ''} onClick={() => setView('kanban')} title="Board">
                <Icon name="view_kanban" size={17} />
              </button>
              <button className={view === 'table' ? 'active' : ''} onClick={() => setView('table')} title="Table">
                <Icon name="table_rows" size={17} />
              </button>
            </div>
            <button className="btn" onClick={() => { setLocal(null); setIncludeTerminal((v) => !v) }}>
              <Icon name="filter_list" size={17} />
              {includeTerminal ? 'Active only' : 'Show closed & dead'}
            </button>
            <button className="btn dashed" onClick={() => nav('/customize')}>
              <Icon name="edit_note" size={17} /> Edit stages
            </button>
            <button className="btn primary" onClick={() => setNewIn('')}>
              <Icon name="add" size={17} /> New loan
            </button>
          </div>
        </div>
      </div>

      {loading && !board ? (
        <Loading label="Loading the loan book…" />
      ) : error ? (
        <div className="page"><ErrorState error={error} onRetry={refresh} /></div>
      ) : view === 'table' ? (
        <div className="page" style={{ paddingTop: 16 }}>
          <div className="card">
            <div className="gt-head" style={{ gridTemplateColumns: GRID }}>
              <span>Borrower</span><span>Stage</span><span className="right">Amount</span>
              <span className="right">Rate</span><span className="right">LTV</span>
              <span>Property</span>
            </div>
            {loans.map((l) => (
              <button className="gt-row" key={l.loan_id} style={{ gridTemplateColumns: GRID }} onClick={() => nav(href(l))}>
                <span>
                  <span className="gt-name">{l.borrower ?? '—'}</span>
                  <span className="gt-sub mono">{l.loan_number ?? (l.source === 'crm' ? 'Pre-LOS' : '')}</span>
                </span>
                <span><span className="badge t-neutral">{labelOf(l.status ?? '')}</span></span>
                <span className="right mono">{usd(l.loan_amount)}</span>
                <span className="right mono">{rate(l.note_rate)}</span>
                <span className="right mono">{percent(l.ltv)}</span>
                <span className="gt-sub">{addressLine(l)}</span>
              </button>
            ))}
          </div>
        </div>
      ) : (
        <div className="board-wrap">
          <div className="board">
            {stages.map((s) => {
              const rows = byStage.get(s.key) ?? []
              const t = board?.totals[s.key]
              return (
                <KanbanColumn
                  key={s.key}
                  label={`${s.label}, ${rows.length}`}
                  onDrop={(id) => move(id, s.key)}
                  header={
                    <header className="kcol-head">
                      <div className="kcol-top">
                        <span className="kswatch" style={{ background: stageColor(s.rolls_up_to || s.key, s.terminal) }} />
                        <span className="kcol-name" title={s.label !== s.canonical_label ? `LendingPad: ${s.canonical_label}` : undefined}>
                          {s.label}
                        </span>
                        <span className="kcol-count">{rows.length}</span>
                        <span className="spacer" />
                        <button className="icon-btn-plain" onClick={() => setNewIn(s.key)} title={`New opportunity in ${s.label}`}>
                          <Icon name="add" size={16} />
                        </button>
                      </div>
                      <div className="kcol-sub">
                        <span className="kcol-value">{usdShort(t?.volume ?? rows.reduce((a, l) => a + (l.loan_amount ?? 0), 0))}</span>
                        <span className="kcol-aging">{dragging ? 'drop here' : rows.length ? `${rows.length} in stage` : 'empty'}</span>
                      </div>
                    </header>
                  }
                >
                  <div className="kcol-body">
                    {rows.map((l) => (
                      <Link
                        to={href(l)}
                        className="deal"
                        key={l.loan_id}
                        style={{ color: 'inherit', display: 'block' }}
                        {...dragProps(l.loan_id, setDragging)}
                      >
                        <div className="deal-top">
                          <span className="deal-name">{l.borrower ?? l.loan_number}</span>
                          <span className="spacer" />
                          {l.source === 'crm' ? <span className="chip-micro" style={{ background: 'var(--accent-tint)', color: 'var(--accent)' }}>Pre-LOS</span> : null}
                          {(l.ltv ?? 0) > 85 ? (
                            <Icon name="priority_high" size={15} style={{ color: 'var(--warning)' }} />
                          ) : null}
                        </div>
                        <div className="deal-amt">{usd(l.loan_amount)}</div>
                        <div className="deal-product">
                          {titleCase(l.purpose)}{l.loan_type ? ` · ${l.loan_type.toUpperCase()}` : ''}
                        </div>
                        {l.note_rate || l.ltv ? (
                          <div className="deal-chips">
                            {l.note_rate ? <span className="chip-mono">{rate(l.note_rate)}</span> : null}
                            {l.ltv ? <span className="chip-mono">LTV {percent(l.ltv)}</span> : null}
                          </div>
                        ) : null}
                        {l.stage_moved ? (
                          <div style={{ fontSize: 11, color: 'var(--warning)', marginTop: 6 }} title="Your stage holds until LendingPad reports a new status">
                            <Icon name="sync_problem" size={12} style={{ verticalAlign: -2 }} /> LendingPad: {labelOf(l.los_status ?? '')}
                          </div>
                        ) : null}
                        <div className="deal-foot">
                          <span><Icon name="schedule" size={13} /> {relativeDays(l.status_at)}</span>
                          <span>{l.property_city ?? ''}</span>
                        </div>
                        <div className="deal-move" onClick={(e) => e.preventDefault()}>
                          <select
                            className="field"
                            value={l.status ?? ''}
                            aria-label={`Move ${l.borrower ?? 'loan'} to stage`}
                            onChange={(e) => move(l.loan_id, e.target.value)}
                          >
                            {stages.map((o) => <option key={o.key} value={o.key}>Move to {o.label}</option>)}
                          </select>
                        </div>
                      </Link>
                    ))}
                  </div>
                </KanbanColumn>
              )
            })}
            <button className="addstage" title="Add a stage of your own" onClick={() => nav('/customize')}>
              <Icon name="add" size={18} />
            </button>
          </div>
        </div>
      )}

      <NewDealModal
        open={newIn !== null}
        onClose={() => setNewIn(null)}
        stages={stages}
        initialStage={newIn || stages[0]?.key}
        onCreated={refresh}
      />
    </div>
  )
}
